// src/api.js

// --------------------
// API base selection
// --------------------
const isProd = process.env.NODE_ENV === "production";
const DEFAULT_DEV_API_BASE = "http://localhost:8000";
const SAME_ORIGIN_API_BASE = "";

const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "")
  .trim()
  .replace(/\/+$/, ""); // remove trailing slashes

// In dev, fall back to localhost if not provided.
const API_BASE = !isProd && !RAW_API_BASE ? DEFAULT_DEV_API_BASE : RAW_API_BASE;

if (isProd && API_BASE && API_BASE.startsWith("http://")) {
  throw new Error("REACT_APP_API_BASE must be https in production.");
}

let activeApiBase = API_BASE;
let switchedToSameOrigin = false;

function buildApiUrl(path, base = activeApiBase) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path}`;
}

function isNetworkFetchError(err) {
  if (err instanceof TypeError) return true;
  return /failed to fetch/i.test(String(err?.message || ""));
}

async function fetchWithApiFallback(path, init) {
  try {
    return await fetch(buildApiUrl(path), init);
  } catch (err) {
    const canFallback =
      isProd &&
      !switchedToSameOrigin &&
      activeApiBase &&
      activeApiBase !== SAME_ORIGIN_API_BASE &&
      isNetworkFetchError(err);

    if (!canFallback) throw err;

    try {
      const res = await fetch(buildApiUrl(path, SAME_ORIGIN_API_BASE), init);
      activeApiBase = SAME_ORIGIN_API_BASE;
      switchedToSameOrigin = true;
      return res;
    } catch {
      throw err;
    }
  }
}

// --------------------
// Response helpers
// --------------------
async function readJsonOrText(res) {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    return { json, text: "" };
  }
  const text = await res.text().catch(() => "");
  return { json: {}, text };
}

function getCookie(name) {
  // Note: this only reads cookies for the CURRENT document domain.
  // In cross-site setups, csrftoken may not be visible here. That's OK.
  const parts = (`; ${document.cookie || ""}`).split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

// --------------------
// CSRF handling
// --------------------
let csrfTokenCache = null;

async function fetchCsrfSeed(path) {
  const res = await fetchWithApiFallback(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const { json, text } = await readJsonOrText(res);
  const token = json?.csrfToken || getCookie("csrftoken") || null;
  if (token) csrfTokenCache = token;

  return { res, json, text, token };
}

export async function ensureCsrf() {
  const existing = getCookie("csrftoken") || csrfTokenCache;
  if (existing) {
    csrfTokenCache = existing;
    return csrfTokenCache;
  }

  let primary = null;
  const csrfSeedPaths = [
    `/api/csrf/?_=${Date.now()}`,
    `/api/csrf/?_=${Date.now()}-${Math.random().toString(36).slice(2)}`,
  ];
  for (const path of csrfSeedPaths) {
    try {
      primary = await fetchCsrfSeed(path);
    } catch {
      primary = null;
    }
    if (primary?.token) return primary.token;
  }

  // Fallback for edge/CDN path-specific blocks: whoami also seeds CSRF.
  let fallback = null;
  try {
    fallback = await fetchCsrfSeed(`/api/auth/whoami/?_=${Date.now()}`);
  } catch {
    fallback = null;
  }
  if (fallback?.token) return fallback.token;

  if (primary?.res && !primary.res.ok) {
    throw new Error(`CSRF fetch failed (${primary.res.status}) ${primary.text || ""}`.trim());
  }
  if (fallback?.res && !fallback.res.ok) {
    throw new Error(`CSRF fallback failed (${fallback.res.status}) ${fallback.text || ""}`.trim());
  }
  throw new Error("CSRF fetch failed (missing token).");
}

function methodNeedsCsrf(method) {
  const m = (method || "GET").toUpperCase();
  return ["POST", "PUT", "PATCH", "DELETE"].includes(m);
}

async function attachCsrf(headers, method) {
  if (!methodNeedsCsrf(method)) return headers;

  // Always hit /api/csrf/ first to make sure csrftoken cookie exists for this browser session.
  await ensureCsrf();

  const token = getCookie("csrftoken") || csrfTokenCache;
  if (token) headers["X-CSRFToken"] = token;

  return headers;
}

// --------------------
// Main fetch wrapper
// --------------------
export async function apiFetch(
  path,
  { method = "GET", body, headers: extraHeaders } = {}
) {
  const upper = (method || "GET").toUpperCase();

  let headers = {
    Accept: "application/json",
    ...(extraHeaders || {}),
  };

  if (body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  headers = await attachCsrf(headers, upper);

  const doFetch = async () => {
    const res = await fetchWithApiFallback(path, {
      method: upper,
      credentials: "include",
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const { json, text } = await readJsonOrText(res);
    return { res, json, text };
  };

  let out = await doFetch();

  // Retry once on likely CSRF failure (commonly: rotated token or missing cookie)
  if (methodNeedsCsrf(upper) && out.res.status === 403) {
    // Refresh cookie/token and retry once
    const fresh = await ensureCsrf();

    headers = {
      ...headers,
      ...(fresh ? { "X-CSRFToken": fresh } : {}),
    };

    out = await doFetch();
  }

  return out;
}

export { API_BASE };
